import React, { useState, useEffect, useRef } from 'react';

const FilterBar = ({ filters, onFilterChange, searchQuery, onSearchChange, filterMode, onFilterModeChange, league = 'nba' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);

  // Close info tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoRef.current && !infoRef.current.contains(event.target)) {
        setShowInfo(false);
      }
    };

    if (showInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfo]);

  const handleSliderChange = (filterName, value) => {
    onFilterChange({
      ...filters,
      [filterName]: parseInt(value)
    });
  };

  const baseFilterConfigs = [
    { key: 'l5', label: 'L5', name: 'Last 5' },
    { key: 'l10', label: 'L10', name: 'Last 10' },
    { key: 'l20', label: 'L20', name: 'Last 20' },
    { key: 'h2h', label: 'H2H', name: 'Head to Head' },
    { key: 'season', label: '2024', name: 'Last Season' },
    { key: 'l50', label: 'L50', name: 'Last 50' }
  ];

  const filterConfigs = baseFilterConfigs.filter(config => {
    if (league === 'nfl' && (config.key === 'l20' || config.key === 'l50')) {
      return false;
    }
    return true;
  });

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    // Count all filters that are greater than 0
    return value > 0;
  }).length;

  const handleReset = () => {
    onFilterChange({
      l5: 0,
      l10: 0,
      l20: 0,
      h2h: 0,
      season: 0,
      l50: 0,
      noVigOver: 0,
      noVigUnder: 0
    });
    onSearchChange('');
  };

  const hasActiveFilters = activeFiltersCount > 0 || searchQuery.trim().length > 0;

  return (
    <div className="filter-bar">
      <div className="filter-bar-header">
        <button 
          className="filter-toggle-btn"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg className="filter-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 5H17.5M5 10H15M7.5 15H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>Filters</span>
          <svg 
            className={`filter-arrow ${isOpen ? 'open' : ''}`}
            width="14" 
            height="14" 
            viewBox="0 0 14 14" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {activeFiltersCount > 0 && (
            <span className="filter-badge">{activeFiltersCount}</span>
          )}
        </button>
        <div className="search-wrapper">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 16.5L12.5 12.5M14.5 8.5C14.5 11.8137 11.8137 14.5 8.5 14.5C5.18629 14.5 2.5 11.8137 2.5 8.5C2.5 5.18629 5.18629 2.5 8.5 2.5C11.8137 2.5 14.5 5.18629 14.5 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="filter-dropdown">
          <div className="filter-mode-section">
            <div className="filter-mode-toggle-wrapper">
              <button 
                className={`filter-mode-btn ${filterMode === 'hit' ? 'active' : ''}`}
                onClick={() => onFilterModeChange('hit')}
              >
                Over
              </button>
              <button 
                className={`filter-mode-btn ${filterMode === 'miss' ? 'active' : ''}`}
                onClick={() => onFilterModeChange('miss')}
              >
                Under
              </button>
            </div>
            <div className="filter-info-wrapper" ref={infoRef}>
              <button 
                className="filter-info-btn"
                onClick={() => setShowInfo(!showInfo)}
                aria-label="Filter mode explanation"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 11V8M8 5H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              {showInfo && (
                <div className="filter-info-tooltip">
                  <div className="filter-info-content">
                    <strong>Over Mode:</strong> Shows players that have hit <strong>over</strong> the selected percentage. For example, 90% means the player has hit more than 90% of the time.
                    <br /><br />
                    <strong>Under Mode:</strong> Shows players that have missed <strong>over</strong> the selected percentage. For example, 90% means the player has missed more than 90% of the time (hit less than 10%).
                    <button 
                      className="filter-info-close"
                      onClick={() => setShowInfo(false)}
                      aria-label="Close info"
                    >
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="sliders-container">
            {filterConfigs.map((config) => (
              <div key={config.key} className="slider-item">
                <div className="slider-header">
                  <div className="slider-label-badge">{config.label}</div>
                  <div className="slider-value-display">
                    {filters[config.key] > 0 ? (
                      <span className="slider-value-active">{filters[config.key]}%</span>
                    ) : (
                      <span className="slider-value-inactive">Any</span>
                    )}
                  </div>
                </div>
                <div className="slider-wrapper">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters[config.key]}
                    onChange={(e) => handleSliderChange(config.key, e.target.value)}
                    className="slider"
                  />
                </div>
              </div>
            ))}
            {/* No-Vig Odds Sliders */}
            <div className="slider-item">
              <div className="slider-header">
                <div className="slider-label-badge">No-Vig Over</div>
                <div className="slider-value-display">
                  {filters.noVigOver > 0 ? (
                    <span className="slider-value-active">{filters.noVigOver}%</span>
                  ) : (
                    <span className="slider-value-inactive">Any</span>
                  )}
                </div>
              </div>
              <div className="slider-wrapper">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={filters.noVigOver || 0}
                  onChange={(e) => handleSliderChange('noVigOver', e.target.value)}
                  className="slider"
                />
              </div>
            </div>
            <div className="slider-item">
              <div className="slider-header">
                <div className="slider-label-badge">No-Vig Under</div>
                <div className="slider-value-display">
                  {filters.noVigUnder > 0 ? (
                    <span className="slider-value-active">{filters.noVigUnder}%</span>
                  ) : (
                    <span className="slider-value-inactive">Any</span>
                  )}
                </div>
              </div>
              <div className="slider-wrapper">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={filters.noVigUnder || 0}
                  onChange={(e) => handleSliderChange('noVigUnder', e.target.value)}
                  className="slider"
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <button className="filter-reset-btn" onClick={handleReset}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Reset Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;

